import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Typography,
  Card,
  Button,
  Stack,
  TextField,
  Box,
  Switch,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ChevronLeft,
  ShieldAlert,
  ChevronRight,
  Search,
  CheckCircle2,
  AlertCircle,
  Key,
} from 'lucide-react';
import { toast } from 'react-toastify';
import Page from '../components/Page';
import {
  getAvailableProcessors,
  createServiceProvider,
  validateServiceProviderCredentials,
} from '../api/services';
import { fCurrency } from '../utils/formatNumber';
import { hasPermission, PERMISSIONS, isAdminOrOwner } from '../utils/hasPermission';
import VendorSelectModal, { getDriverTheme } from './components/Services/VendorSelectModal';

function safeBtoa(str) {
  try {
    return btoa(unescape(encodeURIComponent(str)));
  } catch (e) {
    return btoa(str);
  }
}

export default function ServiceProviderAddPage() {
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.auth);
  const activeApp = useSelector((state) => state.apps?.activeApp);
  const isPrivileged = isAdminOrOwner(user, activeApp);
  const canManage = isPrivileged && hasPermission(user, [PERMISSIONS.SERVICES_WRITE, PERMISSIONS.PROCESSORS_MANAGE]);

  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [processors, setProcessors] = useState([]);
  const [selectedProcessor, setSelectedProcessor] = useState(null);
  const [openVendorModal, setOpenVendorModal] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [adexUsername, setAdexUsername] = useState('');
  const [adexPassword, setAdexPassword] = useState('');
  const [autopilotEmail, setAutopilotEmail] = useState('');

  // Key validation state
  const [testingKey, setTestingKey] = useState(false);
  const [keyValidation, setKeyValidation] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    apiKey: '',
    email: '',
    baseUrl: '',
    driverType: 'msorg',
    authHeaderType: 'Bearer',
    lowBalanceThreshold: 5000,
    isActive: true,
    balance: 0,
    supportsBalanceCheck: true,
    balanceMode: 'api_sync',
  });

  useEffect(() => {
    if (!isPrivileged) {
      setLoadingTemplates(false);
      return;
    }
    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const res = await getAvailableProcessors();
        if (res.ok && res.data?.data?.processors?.length > 0) {
          const list = [...res.data.data.processors];
          if (!list.some((p) => p.name === 'custom')) {
            list.push({
              name: 'custom',
              displayName: 'Custom Provider Gateway',
              driverType: 'custom',
              baseUrl: '',
              authHeaderType: 'Bearer',
              supportedCategories: ['data', 'airtime', 'cable', 'electricity', 'result_check'],
              capabilities: { canFetchBalance: false },
            });
          }
          setProcessors(list);
        } else {
          setProcessors([]);
        }
      } catch (err) {
        setProcessors([]);
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, [isPrivileged]);

  const isAdexDriver =
    (formData.driverType || '').toLowerCase() === 'adex' ||
    (selectedProcessor?.driverType || '').toLowerCase() === 'adex' ||
    (selectedProcessor?.name || '').toLowerCase() === 'datatap' ||
    (selectedProcessor?.name || '').toLowerCase() === 'quicklysim';

  const isAutopilotDriver =
    (formData.driverType || '').toLowerCase() === 'autopilot' ||
    (selectedProcessor?.driverType || '').toLowerCase() === 'autopilot' ||
    (selectedProcessor?.name || '').toLowerCase() === 'autopilot';

  const handleSelectProcessor = (proc) => {
    setSelectedProcessor(proc);
    setKeyValidation(null);
    if (proc) {
      const canCheck = proc.capabilities?.canFetchBalance !== false;
      const isTokenDriver =
        (proc.driverType || '').toLowerCase() === 'adex' ||
        (proc.driverType || '').toLowerCase() === 'msorg' ||
        (proc.name || '').toLowerCase() === 'datatap' ||
        (proc.name || '').toLowerCase() === 'quicklysim';

      setFormData((prev) => ({
        ...prev,
        name: prev.name && prev.name !== selectedProcessor?.displayName ? prev.name : proc.displayName,
        baseUrl: proc.baseUrl || prev.baseUrl,
        driverType: proc.driverType || (isTokenDriver ? 'msorg' : 'custom'),
        authHeaderType: isTokenDriver ? 'Token' : proc.authHeaderType || 'Bearer',
        supportsBalanceCheck: canCheck,
        balanceMode: canCheck ? 'api_sync' : 'manual_ledger',
      }));
    }
  };

  const handleTestKey = async (silentOnSuccess = false) => {
    if (isAdexDriver) {
      if (!adexUsername.trim()) {
        toast.error('Please enter your portal username or email first.');
        return false;
      }
      if (!adexPassword) {
        toast.error('Please enter your portal password first.');
        return false;
      }
    } else if (!formData.apiKey.trim()) {
      toast.error('Please enter your vendor API key first.');
      return false;
    }

    if (!formData.baseUrl.trim()) {
      toast.error('Please select a vendor template to configure the base URL.');
      return false;
    }

    setTestingKey(true);
    try {
      const emailVal = (formData.email || autopilotEmail || '').trim();
      let resolvedApiKey = formData.apiKey.trim();
      if (isAdexDriver) {
        resolvedApiKey = safeBtoa(`${adexUsername.trim()}:${adexPassword}`);
      } else if (isAutopilotDriver && emailVal) {
        resolvedApiKey = resolvedApiKey.includes(':')
          ? resolvedApiKey
          : `${emailVal}:${resolvedApiKey}`;
      }

      let resolvedUsername;
      if (isAutopilotDriver && emailVal) {
        resolvedUsername = emailVal;
      } else if (isAdexDriver) {
        resolvedUsername = adexUsername.trim();
      }

      const payload = {
        name: formData.name.trim() || selectedProcessor?.displayName || 'Verification',
        driverType: isAdexDriver ? 'adex' : formData.driverType || 'msorg',
        baseUrl: formData.baseUrl.trim(),
        apiKey: resolvedApiKey,
        email: emailVal || undefined,
        username: resolvedUsername,
        authHeaderType: isAdexDriver ? 'Token' : formData.authHeaderType || 'Bearer',
      };

      const res = await validateServiceProviderCredentials(payload);
      if (res.ok && res.data?.data?.valid) {
        const { data } = res.data;
        setKeyValidation({
          status: 'success',
          message: data.message || 'Credentials verified successfully.',
          balance: data.balance ?? 0,
          username: data.username || (isAdexDriver ? adexUsername.trim() : null),
        });

        if (data.tokenType) {
          setFormData((prev) => ({ ...prev, authHeaderType: data.tokenType }));
        }

        if (data.balance !== undefined && !Number.isNaN(Number(data.balance))) {
          setFormData((prev) => ({ ...prev, balance: Number(data.balance) }));
        }

        if (!silentOnSuccess) {
          toast.success(`Credentials verified! Live balance: ₦${fCurrency(data.balance ?? 0)}`);
        }
        return true;
      }

      const errMsg = res.data?.message || 'Failed to verify credentials with vendor.';
      setKeyValidation({
        status: 'error',
        message: errMsg,
      });
      toast.error(errMsg);
      return false;
    } catch (err) {
      const errMsg =
        err.response?.data?.message ||
        err.message ||
        'Error connecting to vendor for verification.';
      setKeyValidation({
        status: 'error',
        message: errMsg,
      });
      toast.error(errMsg);
      return false;
    } finally {
      setTestingKey(false);
    }
  };

  const renderVerificationStatus = () => {
    if (keyValidation?.status === 'success') {
      const usernameDisplay = keyValidation.username ? `Connected as "${keyValidation.username}". ` : '';
      return (
        <Box
          sx={{
            p: 2,
            borderRadius: '12px',
            bgcolor: '#F0FDF4',
            border: '1px solid #BBF7D0',
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  bgcolor: '#DCFCE7',
                  color: '#16A34A',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  mt: 0.25,
                }}
              >
                <CheckCircle2 size={16} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#166534' }}>
                  Credentials Verified
                </Typography>
                <Typography sx={{ fontSize: '0.775rem', color: '#15803D', mt: 0.25 }}>
                  {usernameDisplay}
                  Vendor confirmed account is active and valid.
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Box
                    sx={{
                      px: 1.25,
                      py: 0.4,
                      borderRadius: '6px',
                      bgcolor: '#DCFCE7',
                      border: '1px solid #86EFAC',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                    }}
                  >
                    <Typography sx={{ fontSize: '0.725rem', fontWeight: 600, color: '#166534' }}>
                      Live Wallet Balance:
                    </Typography>
                    <Typography sx={{ fontSize: '0.775rem', fontWeight: 800, color: '#14532D' }}>
                      ₦{fCurrency(keyValidation.balance ?? 0)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Stack>
            <Button
              size="small"
              variant="text"
              disabled={testingKey}
              onClick={() => handleTestKey(false)}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#15803D',
                p: 0.5,
                minWidth: 'auto',
                '&:hover': { bgcolor: '#DCFCE7' },
              }}
            >
              Re-test
            </Button>
          </Stack>
        </Box>
      );
    }

    if (keyValidation?.status === 'error') {
      return (
        <Box
          sx={{
            p: 2,
            borderRadius: '12px',
            bgcolor: '#FEF2F2',
            border: '1px solid #FECACA',
          }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '8px',
                  bgcolor: '#FEE2E2',
                  color: '#DC2626',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  mt: 0.25,
                }}
              >
                <AlertCircle size={16} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#991B1B' }}>
                  Credential Verification Failed
                </Typography>
                <Typography sx={{ fontSize: '0.775rem', color: '#B91C1C', mt: 0.25 }}>
                  {keyValidation.message}
                </Typography>
                <Typography sx={{ fontSize: '0.725rem', color: '#7F1D1D', mt: 0.5 }}>
                  Please double-check your credentials and ensure API access is active.
                </Typography>
              </Box>
            </Stack>
            <Button
              size="small"
              variant="contained"
              disabled={testingKey}
              onClick={() => handleTestKey(false)}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                borderRadius: '8px',
                bgcolor: '#DC2626',
                color: '#FFFFFF',
                '&:hover': { bgcolor: '#B91C1C' },
                flexShrink: 0,
              }}
            >
              {testingKey ? <CircularProgress size={14} color="inherit" /> : 'Retry'}
            </Button>
          </Stack>
        </Box>
      );
    }

    return (
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography sx={{ fontSize: '0.825rem', fontWeight: 600, color: '#374151' }}>
            Test Credentials
          </Typography>
          <Typography variant="caption" sx={{ color: '#6B7280' }}>
            Verify your key against the vendor to confirm live balance before saving.
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          disabled={testingKey}
          onClick={() => handleTestKey(false)}
          startIcon={testingKey ? <CircularProgress size={14} /> : <Key size={14} />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8rem',
            borderRadius: '10px',
            borderColor: '#D1D5DB',
            color: '#374151',
            flexShrink: 0,
            px: 2,
            py: 0.75,
            '&:hover': { borderColor: '#0066FE', color: '#0066FE', bgcolor: '#EFF6FF' },
          }}
        >
          {testingKey ? 'Testing...' : 'Test Key'}
        </Button>
      </Stack>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPrivileged || !canManage) {
      toast.error('Only administrators and owners can add service providers.');
      return;
    }

    if (!formData.name.trim()) {
      toast.error('Please enter an account name.');
      return;
    }

    if (isAdexDriver) {
      if (!adexUsername.trim()) {
        toast.error('Please enter your portal username or email.');
        return;
      }
      if (!adexPassword) {
        toast.error('Please enter your portal password.');
        return;
      }
    } else if (!formData.apiKey.trim()) {
      toast.error('Please enter your vendor API key.');
      return;
    }

    // Automatically validate credentials before creating if not yet verified
    if (keyValidation?.status !== 'success') {
      const isValid = await handleTestKey(true);
      if (!isValid) {
        return;
      }
    }

    setSubmitting(true);
    try {
      const emailVal = (formData.email || autopilotEmail || '').trim();
      let resolvedApiKey = formData.apiKey.trim();
      if (isAdexDriver) {
        resolvedApiKey = safeBtoa(`${adexUsername.trim()}:${adexPassword}`);
      } else if (isAutopilotDriver && emailVal) {
        resolvedApiKey = resolvedApiKey.includes(':')
          ? resolvedApiKey
          : `${emailVal}:${resolvedApiKey}`;
      }

      const payload = {
        name: formData.name.trim(),
        slug: selectedProcessor?.name || undefined,
        providerId: selectedProcessor?._id || undefined,
        driverConfig: selectedProcessor
          ? {
              customRoutes: selectedProcessor.customRoutes || null,
              customConfig: selectedProcessor.customConfig || null,
              capabilities: selectedProcessor.capabilities || null,
            }
          : undefined,
        driverType: isAdexDriver ? 'adex' : formData.driverType || 'msorg',
        baseUrl: formData.baseUrl.trim(),
        apiKey: resolvedApiKey,
        email: emailVal || undefined,
        authHeaderType: isAdexDriver ? 'Token' : formData.authHeaderType || 'Bearer',
        lowBalanceThreshold: Number(formData.lowBalanceThreshold) || 5000,
        isActive: formData.isActive,
        balance: Number(formData.balance) >= 0 ? Number(formData.balance) : 0,
        supportsBalanceCheck: formData.supportsBalanceCheck,
        balanceMode: formData.balanceMode,
      };

      const res = await createServiceProvider(payload);
      if (res.ok) {
        toast.success(`Service provider "${formData.name}" added successfully!`);
        navigate('/dashboard/service-providers');
      } else {
        toast.error(res.data?.message || 'Failed to add service provider.');
      }
    } catch (err) {
      toast.error('An unexpected error occurred while creating service provider.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderVendorTriggerContent = () => {
    if (loadingTemplates) {
      return (
        <Box display="flex" alignItems="center" gap={1.5} py={0.5}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading vendor templates...
          </Typography>
        </Box>
      );
    }

    if (selectedProcessor) {
      const theme = getDriverTheme(selectedProcessor.driverType);
      const IconComp = theme.icon;
      return (
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.75} sx={{ minWidth: 0, flex: 1 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                bgcolor: theme.bg,
                color: theme.text,
                border: `1px solid ${theme.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <IconComp size={22} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', mb: 0.25 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#111827' }}>
                  {selectedProcessor.displayName || selectedProcessor.name}
                </Typography>
              </Stack>
              <Typography
                variant="caption"
                sx={{
                  color: '#6B7280',
                  fontFamily: 'monospace',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {selectedProcessor.baseUrl || 'Custom endpoint required'}
              </Typography>
            </Box>
          </Stack>
          <Button
            size="small"
            variant="outlined"
            endIcon={<ChevronRight size={14} />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.785rem',
              borderRadius: '10px',
              borderColor: '#E5E7EB',
              color: '#374151',
              '&:hover': { borderColor: '#0066FE', color: '#0066FE', bgcolor: '#EFF6FF' },
            }}
          >
            Change
          </Button>
        </Stack>
      );
    }

    return (
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1.75}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              bgcolor: '#F3F4F6',
              color: '#6B7280',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Search size={22} />
          </Box>
          <div>
            <Typography sx={{ fontWeight: 600, fontSize: '0.925rem', color: '#111827' }}>
              Choose a vendor template
            </Typography>
            <Typography variant="caption" sx={{ color: '#6B7280' }}>
              Browse QuicklySim, Datatap, SMEPlug, or custom gateways
            </Typography>
          </div>
        </Stack>
        <Button
          size="small"
          variant="contained"
          endIcon={<ChevronRight size={14} />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.785rem',
            borderRadius: '10px',
            bgcolor: '#0066FE',
            '&:hover': { bgcolor: '#0052CC' },
          }}
        >
          Browse Catalog
        </Button>
      </Stack>
    );
  };

  const isFormValid =
    formData.name.trim().length > 0 &&
    (isAdexDriver
      ? adexUsername.trim().length > 0 && adexPassword.length > 0
      : formData.apiKey.trim().length > 0);

  if (!isPrivileged) {
    return (
      <Page title="Access Denied | Service Providers">
        <Container maxWidth="lg" sx={{ py: 6 }}>
          <Card sx={{ p: 4, textAlign: 'center', borderRadius: '16px', border: '1px solid #FECACA', bgcolor: '#FEF2F2' }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: '#FEE2E2', color: '#DC2626', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <ShieldAlert size={24} />
            </Box>
            <Typography variant="h6" sx={{ color: '#991B1B', fontWeight: 700, mb: 1 }}>
              Restricted Security Settings
            </Typography>
            <Typography variant="body2" sx={{ color: '#7F1D1D', maxWidth: 480, mx: 'auto' }}>
              Service provider credentials and configurations are strictly restricted to Workspace Administrators and Owners.
            </Typography>
          </Card>
        </Container>
      </Page>
    );
  }

  return (
    <Page title="Add Service Provider | Dashboard">
      <Container maxWidth="sm" sx={{ py: 3, maxWidth: '640px !important' }}>
        {/* Top Back Link */}
        <Typography
          component="button"
          onClick={() => navigate('/dashboard/service-providers')}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            background: 'none',
            border: 'none',
            p: 0,
            color: '#0066FE',
            fontWeight: 500,
            fontSize: '0.925rem',
            cursor: 'pointer',
            mb: 2,
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <ChevronLeft size={16} /> Service providers
        </Typography>

        {/* Title & Subtitle */}
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            color: '#111827',
            fontSize: { xs: '1.75rem', sm: '2.15rem' },
            letterSpacing: '-0.02em',
            mb: 0.5,
          }}
        >
          Add service provider
        </Typography>
        <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.925rem', mb: 3.5 }}>
          Connect a telecom vendor to power your data, airtime and utility services.
        </Typography>

        {!canManage && (
          <Alert severity="warning" icon={<ShieldAlert size={20} />} sx={{ mb: 3 }}>
            <strong>Read-only access:</strong> You do not have permissions to add service providers (Requires{' '}
            <code>processors:manage</code> or <code>services:write</code>).
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Section 1: Vendor Template */}
          <Box mb={3.5}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#374151' }}>
                Vendor Gateway
              </Typography>
              {selectedProcessor && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setOpenVendorModal(true)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    color: '#0066FE',
                    p: 0,
                    minWidth: 'auto',
                    '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                  }}
                >
                  Change template
                </Button>
              )}
            </Box>

            <Card
              onClick={() => setOpenVendorModal(true)}
              sx={{
                p: 2.25,
                borderRadius: '16px',
                bgcolor: '#FFFFFF',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                border: selectedProcessor ? '1.5px solid #BFDBFE' : '1px dashed #D1D5DB',
                cursor: 'pointer',
                transition: 'all 0.15s ease-in-out',
                '&:hover': {
                  borderColor: '#0066FE',
                  bgcolor: '#FAFCFF',
                  boxShadow: '0 4px 12px rgba(0, 102, 254, 0.05)',
                },
              }}
            >
              {renderVendorTriggerContent()}
            </Card>

            <Typography sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 1 }}>
              Choosing a vendor pre-populates base endpoints, auth credentials and balance tracking configurations.
            </Typography>
          </Box>

          {/* Section 2: Account Details */}
          <Box mb={3.5}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#374151', mb: 1 }}>Account</Typography>

            <Card
              sx={{
                borderRadius: '16px',
                bgcolor: '#FFFFFF',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                border: '1px solid #ECEEF1',
                overflow: 'hidden',
              }}
            >
              {/* Account name */}
              <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #F3F4F6' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 500, mb: 0.5 }}>
                  Account name <span style={{ color: '#DC2626' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  placeholder="e.g. Kyb DATA SUB main"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  sx={{
                    '& input': {
                      p: 0,
                      fontSize: '0.925rem',
                      color: '#111827',
                      '&::placeholder': { color: '#9CA3AF', opacity: 1 },
                    },
                  }}
                />
              </Box>

              {/* Credential input: Username & Password for Adex, or API Key for others */}
              {isAdexDriver ? (
                <>
                  {/* Adex Portal Username */}
                  <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #F3F4F6' }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 500, mb: 0.5 }}>
                      Portal Username / Email <span style={{ color: '#DC2626' }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      variant="standard"
                      InputProps={{ disableUnderline: true }}
                      placeholder="e.g. johndoe or user@email.com"
                      value={adexUsername}
                      onChange={(e) => {
                        setAdexUsername(e.target.value);
                        if (keyValidation) setKeyValidation(null);
                      }}
                      sx={{
                        '& input': {
                          p: 0,
                          fontSize: '0.925rem',
                          color: '#111827',
                          '&::placeholder': { color: '#9CA3AF', opacity: 1 },
                        },
                      }}
                    />
                  </Box>

                  {/* Adex Portal Password */}
                  <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #F3F4F6' }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 500, mb: 0.5 }}>
                      Portal Password <span style={{ color: '#DC2626' }}>*</span>
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <TextField
                        fullWidth
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your account password"
                        value={adexPassword}
                        onChange={(e) => {
                          setAdexPassword(e.target.value);
                          if (keyValidation) setKeyValidation(null);
                        }}
                        sx={{
                          '& input': {
                            p: 0,
                            fontSize: '0.925rem',
                            color: '#111827',
                            '&::placeholder': { color: '#9CA3AF', opacity: 1 },
                          },
                        }}
                      />
                      <Typography
                        component="button"
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        sx={{
                          background: 'none',
                          border: 'none',
                          p: 0,
                          ml: 2,
                          color: '#0066FE',
                          fontWeight: 500,
                          fontSize: '0.875rem',
                          cursor: 'pointer',
                          flexShrink: 0,
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </Typography>
                    </Box>
                  </Box>
                </>
              ) : (
                <>
                  {/* Account / Vendor Email */}
                  <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #F3F4F6' }}>
                    <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 500, mb: 0.5 }}>
                      Account / Vendor Email{' '}
                      {isAutopilotDriver ? (
                        <span style={{ color: '#059669', fontSize: '0.7rem' }}>(Required to sync Autopilot wallet balance)</span>
                      ) : (
                        <span style={{ color: '#9CA3AF', fontSize: '0.7rem' }}>(Optional)</span>
                      )}
                    </Typography>
                    <TextField
                      fullWidth
                      variant="standard"
                      InputProps={{ disableUnderline: true }}
                      placeholder="e.g. user@vendor.com"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        if (keyValidation) setKeyValidation(null);
                      }}
                      sx={{
                        '& input': {
                          p: 0,
                          fontSize: '0.925rem',
                          color: '#111827',
                          '&::placeholder': { color: '#9CA3AF', opacity: 1 },
                        },
                      }}
                    />
                  </Box>

                  {/* Standard API key */}
                  <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #F3F4F6' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 500, mb: 0.5 }}>
                    API key <span style={{ color: '#DC2626' }}>*</span>
                  </Typography>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <TextField
                      fullWidth
                      variant="standard"
                      InputProps={{ disableUnderline: true }}
                      type={showApiKey ? 'text' : 'password'}
                      placeholder="Paste your vendor API key or token"
                      value={formData.apiKey}
                      onChange={(e) => {
                        setFormData({ ...formData, apiKey: e.target.value });
                        if (keyValidation) setKeyValidation(null);
                      }}
                      sx={{
                        '& input': {
                          p: 0,
                          fontSize: '0.925rem',
                          color: '#111827',
                          '&::placeholder': { color: '#9CA3AF', opacity: 1 },
                        },
                      }}
                    />
                    <Typography
                      component="button"
                      type="button"
                      onClick={() => setShowApiKey(!showApiKey)}
                      sx={{
                        background: 'none',
                        border: 'none',
                        p: 0,
                        ml: 2,
                        color: '#0066FE',
                        fontWeight: 500,
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        flexShrink: 0,
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      {showApiKey ? 'Hide' : 'Show'}
                    </Typography>
                  </Box>
                </Box>
                </>
              )}

              {/* API base URL (Disabled for tenant) */}
              <Box sx={{ px: 2.5, py: 1.75, bgcolor: '#F9FAFB', borderBottom: '1px solid #F3F4F6' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 500 }}>
                    API base URL
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#9CA3AF', fontSize: '0.7rem' }}>
                    Managed automatically
                  </Typography>
                </Stack>
                <TextField
                  fullWidth
                  disabled
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  placeholder="Select a vendor template to set base endpoint"
                  value={formData.baseUrl}
                  sx={{
                    '& input': {
                      p: 0,
                      fontSize: '0.885rem',
                      fontFamily: 'monospace',
                      color: '#4B5563',
                      WebkitTextFillColor: '#4B5563',
                      cursor: 'not-allowed',
                      '&::placeholder': { color: '#9CA3AF', opacity: 1 },
                    },
                  }}
                />
              </Box>

              {/* Credential Verification Status & Action */}
              <Box sx={{ px: 2.5, py: 2, bgcolor: '#FAFBFC' }}>
                {renderVerificationStatus()}
              </Box>
            </Card>

            <Typography sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 1 }}>
              {isAdexDriver
                ? 'This vendor authenticates securely via portal account credentials. Base URLs and tokens are managed automatically.'
                : 'Find your API key in the vendor dashboard under API or Developer settings. Base URLs are managed automatically.'}
            </Typography>
          </Box>

          {/* Section 3: Preferences */}
          <Box mb={3.5}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#374151', mb: 1 }}>Preferences</Typography>

            <Card
              sx={{
                borderRadius: '16px',
                bgcolor: '#FFFFFF',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                border: '1px solid #ECEEF1',
                overflow: 'hidden',
              }}
            >
              {/* Low balance alert */}
              <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #F3F4F6' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 500, mb: 0.5 }}>
                  Low balance alert
                </Typography>
                <Box display="flex" alignItems="center">
                  <Typography sx={{ color: '#6B7280', fontSize: '0.95rem', mr: 1, fontWeight: 500 }}>₦</Typography>
                  <TextField
                    fullWidth
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    type="number"
                    value={formData.lowBalanceThreshold}
                    onChange={(e) => setFormData({ ...formData, lowBalanceThreshold: e.target.value })}
                    sx={{
                      '& input': {
                        p: 0,
                        fontSize: '0.95rem',
                        color: '#111827',
                        fontWeight: 500,
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Route orders switch */}
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.75 }}>
                <div>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#111827' }}>
                    Route orders to this vendor
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: '#6B7280', mt: 0.25 }}>
                    Turn off to pause live orders.
                  </Typography>
                </div>
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  sx={{
                    width: 48,
                    height: 26,
                    padding: 0,
                    '& .MuiSwitch-switchBase': {
                      padding: 0,
                      margin: '2px',
                      transitionDuration: '200ms',
                      '&.Mui-checked': {
                        transform: 'translateX(22px)',
                        color: '#fff',
                        '& + .MuiSwitch-track': {
                          backgroundColor: '#22C55E',
                          opacity: 1,
                          border: 0,
                        },
                      },
                    },
                    '& .MuiSwitch-thumb': {
                      boxSizing: 'border-box',
                      width: 22,
                      height: 22,
                    },
                    '& .MuiSwitch-track': {
                      borderRadius: 13,
                      backgroundColor: '#E5E7EB',
                      opacity: 1,
                    },
                  }}
                />
              </Box>
            </Card>

            <Typography sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 1 }}>
              You get an alert when the wallet drops below this amount.
            </Typography>
          </Box>

          {/* Section 4: Balance & Ledger Tracking */}
          <Box mb={3.5}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: '#374151', mb: 1 }}>
              Balance & Ledger Tracking
            </Typography>

            <Card
              sx={{
                borderRadius: '16px',
                bgcolor: '#FFFFFF',
                boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                border: '1px solid #ECEEF1',
                overflow: 'hidden',
              }}
            >
              {/* Balance Mode Selection */}
              <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #F3F4F6' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 500, mb: 0.5 }}>
                  Balance Tracking Mode
                </Typography>
                <Select
                  fullWidth
                  variant="standard"
                  disableUnderline
                  value={formData.balanceMode}
                  onChange={(e) => {
                    const mode = e.target.value;
                    setFormData({
                      ...formData,
                      balanceMode: mode,
                      supportsBalanceCheck: mode === 'api_sync',
                    });
                  }}
                  sx={{
                    fontSize: '0.925rem',
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  <MenuItem value="api_sync">Live API Synchronized (Periodically checks vendor server)</MenuItem>
                  <MenuItem value="manual_ledger">Manual Virtual Ledger (Auto-decrements per order checkout)</MenuItem>
                  <MenuItem value="unlimited">Unlimited Balance Mode (Post-paid vendor contract)</MenuItem>
                </Select>
              </Box>

              {/* Initial Balance */}
              <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #F3F4F6' }}>
                <Typography sx={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: 500, mb: 0.5 }}>
                  Initial Wallet Balance
                </Typography>
                <Box display="flex" alignItems="center">
                  <Typography sx={{ color: '#6B7280', fontSize: '0.95rem', mr: 1, fontWeight: 500 }}>₦</Typography>
                  <TextField
                    fullWidth
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    type="number"
                    placeholder="0.00"
                    value={formData.balance}
                    onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                    sx={{
                      '& input': {
                        p: 0,
                        fontSize: '0.95rem',
                        color: '#111827',
                        fontWeight: 600,
                      },
                    }}
                  />
                </Box>
              </Box>

              {/* Automated Balance Polling switch */}
              <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.75 }}>
                <div>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#111827' }}>
                    Automated API balance check
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: '#6B7280', mt: 0.25 }}>
                    {formData.supportsBalanceCheck
                      ? 'Queries upstream vendor periodically to sync live balance.'
                      : 'Disabled: Cron sync will not overwrite manual ledger balance.'}
                  </Typography>
                </div>
                <Switch
                  checked={formData.supportsBalanceCheck}
                  disabled={formData.balanceMode === 'unlimited'}
                  onChange={(e) => {
                    const { checked } = e.target;
                    setFormData({
                      ...formData,
                      supportsBalanceCheck: checked,
                      balanceMode: checked ? 'api_sync' : 'manual_ledger',
                    });
                  }}
                  sx={{
                    width: 48,
                    height: 26,
                    padding: 0,
                    '& .MuiSwitch-switchBase': {
                      padding: 0,
                      margin: '2px',
                      transitionDuration: '200ms',
                      '&.Mui-checked': {
                        transform: 'translateX(22px)',
                        color: '#fff',
                        '& + .MuiSwitch-track': {
                          backgroundColor: '#0066FE',
                          opacity: 1,
                          border: 0,
                        },
                      },
                    },
                    '& .MuiSwitch-thumb': {
                      boxSizing: 'border-box',
                      width: 22,
                      height: 22,
                    },
                    '& .MuiSwitch-track': {
                      borderRadius: 13,
                      backgroundColor: '#E5E7EB',
                      opacity: 1,
                    },
                  }}
                />
              </Box>
            </Card>
            <Typography sx={{ color: '#6B7280', fontSize: '0.8rem', mt: 1 }}>
              For vendors without a check-balance API, select Manual Virtual Ledger to track funds internally.
            </Typography>
          </Box>

          {/* Section 5: Security Callout */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 2.25,
              borderRadius: '16px',
              bgcolor: '#EFF6FF',
              color: '#1E40AF',
              mb: 4,
            }}
          >
            <Box sx={{ fontSize: '1.1rem', lineHeight: 1 }}>🔒</Box>
            <Typography sx={{ fontSize: '0.825rem', color: '#1E40AF', lineHeight: 1.45 }}>
              Your API credentials are encrypted and stored securely. Balance syncs and health checks run automatically.
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Stack direction="row" spacing={1.5} justifyContent="flex-end" mb={6}>
            <Button
              type="button"
              onClick={() => navigate('/dashboard/service-providers')}
              disabled={submitting}
              sx={{
                borderRadius: '24px',
                bgcolor: '#EAECEF',
                color: '#1F2937',
                px: 3,
                py: 1.1,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#DFE2E6',
                  boxShadow: 'none',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !isFormValid}
              sx={{
                borderRadius: '24px',
                bgcolor: isFormValid ? '#0066FE' : '#A5C3FF',
                color: '#FFFFFF',
                px: 3.5,
                py: 1.1,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: isFormValid ? '#0052CC' : '#A5C3FF',
                  boxShadow: 'none',
                },
                '&.Mui-disabled': {
                  bgcolor: '#A5C3FF',
                  color: '#FFFFFF',
                  opacity: 0.8,
                },
              }}
            >
              {submitting ? 'Creating...' : 'Create provider'}
            </Button>
          </Stack>
        </form>

        <VendorSelectModal
          open={openVendorModal}
          onClose={() => setOpenVendorModal(false)}
          processors={processors}
          selectedProcessor={selectedProcessor}
          onSelectProcessor={handleSelectProcessor}
          loading={loadingTemplates}
        />
      </Container>
    </Page>
  );
}
